import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  FileText, Plus, Edit2, Trash2, Eye, Copy, Lock, Unlock, 
  ChevronRight, Loader2, BookTemplate, Building2, Shield
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const AdminProcessTemplates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [sources, setSources] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFromSourceModal, setShowFromSourceModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(null); // Template ID to apply
  const [selectedCompany, setSelectedCompany] = useState('');
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '', is_private: false });
  const [selectedSource, setSelectedSource] = useState('');
  const [sourceTemplateName, setSourceTemplateName] = useState('');
  const [sourceTemplateDesc, setSourceTemplateDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    fetchTemplates();
    if (isMainAdmin) {
      fetchSources();
      fetchCompanies();
    }
  }, [isMainAdmin]);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/admin/process-templates');
      setTemplates(res.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/admin/companies');
      setCompanies(res.data.filter(c => !c.is_template));
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const res = await api.get('/admin/process-templates/sources/list');
      setSources(res.data);
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/admin/process-templates', newTemplate);
      toast.success('Template created successfully');
      setShowCreateModal(false);
      setNewTemplate({ name: '', description: '', is_private: false });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFromSource = async () => {
    if (!selectedSource || !sourceTemplateName.trim()) {
      toast.error('Please select a source and enter a template name');
      return;
    }
    
    setCreating(true);
    try {
      const endpoint = selectedSource === 'main_admin' 
        ? '/admin/process-templates/from-main-admin'
        : `/admin/process-templates/from-company/${selectedSource}`;
      
      const res = await api.post(endpoint, {
        name: sourceTemplateName,
        description: sourceTemplateDesc
      });
      
      toast.success(res.data.message);
      setShowFromSourceModal(false);
      setSelectedSource('');
      setSourceTemplateName('');
      setSourceTemplateDesc('');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template from source');
    } finally {
      setCreating(false);
    }
  };

  const handleTogglePrivate = async (template) => {
    try {
      await api.put(`/admin/process-templates/${template.id}`, {
        is_private: !template.is_private
      });
      toast.success(`Template is now ${template.is_private ? 'public' : 'private'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/admin/process-templates/${templateId}`);
      toast.success('Template deleted');
      setDeleteConfirm(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleApplyToCompany = async () => {
    if (!selectedCompany || !showApplyModal) {
      toast.error('Please select a company');
      return;
    }
    
    setApplyingTemplate(true);
    try {
      const res = await api.post(`/admin/process-templates/${showApplyModal}/apply-to-company/${selectedCompany}`);
      toast.success(res.data.message);
      setShowApplyModal(null);
      setSelectedCompany('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply template');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const handleCopyTemplate = async (templateId) => {
    // For company admins to load template into their company
    try {
      const res = await api.post(`/admin/process-templates/${templateId}/copy`);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load template');
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Process Templates</h1>
            <p className="text-gray-500 mt-1">Create and manage reusable process templates for companies</p>
          </div>
          
          {isMainAdmin && (
            <div className="flex gap-3">
              <button
                onClick={() => setShowFromSourceModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="create-from-source-btn"
              >
                <Copy className="w-4 h-4" />
                Create from Existing
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors"
                data-testid="create-template-btn"
              >
                <Plus className="w-4 h-4" />
                New Template
              </button>
            </div>
          )}
        </div>

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <BookTemplate className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Templates Yet</h3>
            <p className="text-gray-500 mb-6">Create your first process template to get started</p>
            {isMainAdmin && (
              <button
                onClick={() => setShowFromSourceModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                <Plus className="w-4 h-4" />
                Create Template
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map(template => (
              <div
                key={template.id}
                className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${template.is_private ? 'bg-gray-100' : 'bg-orange-100'}`}>
                      <FileText className={`w-5 h-5 ${template.is_private ? 'text-gray-600' : 'text-orange-600'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <span className="text-xs text-gray-500">v{template.version || 1}</span>
                    </div>
                  </div>
                  
                  {template.is_private && (
                    <span className="flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                      <Lock className="w-3 h-3" />
                      Private
                    </span>
                  )}
                </div>
                
                {template.description && (
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{template.description}</p>
                )}
                
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1.5">
                    <FileText className="w-4 h-4" />
                    {template.step_count || 0} steps
                  </span>
                  <span>{template.question_count || 0} questions</span>
                </div>

                {template.source_company_name && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                    <Building2 className="w-3 h-3" />
                    From: {template.source_company_name}
                  </div>
                )}
                
                {isMainAdmin ? (
                  <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/admin/process-templates/${template.id}`)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleTogglePrivate(template)}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title={template.is_private ? 'Make Public' : 'Make Private'}
                      >
                        {template.is_private ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(template.id)}
                        className="flex items-center justify-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => setShowApplyModal(template.id)}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition-colors"
                      data-testid={`apply-template-${template.id}`}
                    >
                      <Copy className="w-4 h-4" />
                      Apply to Company
                    </button>
                  </div>
                ) : (
                  <div className="pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleCopyTemplate(template.id)}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-orange-600 text-white hover:bg-orange-700 rounded-lg transition-colors"
                      data-testid={`use-template-${template.id}`}
                    >
                      <Copy className="w-4 h-4" />
                      Use This Template
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create Template Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <h2 className="text-xl font-semibold mb-4">Create New Template</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                  <input
                    type="text"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                    placeholder="e.g., Full Commodity Trade Process"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="template-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                    placeholder="Describe what this template is for..."
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                </div>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTemplate.is_private}
                    onChange={(e) => setNewTemplate({...newTemplate, is_private: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Make this template private (only visible to Main Admin)</span>
                </label>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTemplate}
                  disabled={creating}
                  className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create from Source Modal */}
        {showFromSourceModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6">
              <h2 className="text-xl font-semibold mb-2">Create Template from Existing Process</h2>
              <p className="text-sm text-gray-500 mb-6">Copy steps and questions from Main Admin or a company</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Source</label>
                  <select
                    value={selectedSource}
                    onChange={(e) => {
                      setSelectedSource(e.target.value);
                      const source = sources.find(s => s.id === e.target.value);
                      if (source) {
                        setSourceTemplateName(source.type === 'main_admin' ? 'Platform Default Template' : `${source.name} Template`);
                        setSourceTemplateDesc(source.type === 'main_admin' 
                          ? 'Based on platform default process' 
                          : `Based on ${source.name}'s process`
                        );
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="source-select"
                  >
                    <option value="">Select a source...</option>
                    {sources.map(source => (
                      <option key={source.id} value={source.id}>
                        {source.type === 'main_admin' ? '⭐ ' : '🏢 '}
                        {source.name} ({source.step_count} steps, {source.question_count} questions)
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                  <input
                    type="text"
                    value={sourceTemplateName}
                    onChange={(e) => setSourceTemplateName(e.target.value)}
                    placeholder="Enter template name"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={sourceTemplateDesc}
                    onChange={(e) => setSourceTemplateDesc(e.target.value)}
                    placeholder="Describe this template..."
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowFromSourceModal(false);
                    setSelectedSource('');
                    setSourceTemplateName('');
                    setSourceTemplateDesc('');
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFromSource}
                  disabled={creating || !selectedSource}
                  className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete Template?</h3>
              <p className="text-gray-500 text-sm mb-6">
                This will permanently delete the template and all its steps and questions. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteTemplate(deleteConfirm)}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Apply to Company Modal */}
        {showApplyModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <Building2 className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Apply Template to Company</h2>
                  <p className="text-sm text-gray-500">Select a company to apply this template</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Company</label>
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="select-company"
                  >
                    <option value="">Choose a company...</option>
                    <option value="main">⭐ Main Admin (Self)</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.subdomain})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Important</p>
                      <p>This will replace all existing steps and questions for the selected company. Existing quotes will continue using their original process steps.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowApplyModal(null);
                    setSelectedCompany('');
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyToCompany}
                  disabled={applyingTemplate || !selectedCompany}
                  className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {applyingTemplate ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    'Apply Template'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminProcessTemplates;
